import { useApi } from '@src/app/context/Api';
import { useRoutesContext } from '@src/app/context/Routes/Routes';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Loader } from '@src/shared/ui/components/Loader';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { motion } from 'framer-motion';
import { isEmpty, pull } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import { Filters, Tabs } from './components';

/**
 * Page: OperationsDashboard
 *
 * Provides a screen to visualize a risk trend report.
 *
 * @returns {Element}
 * @constructor
 */
export function OperationsDashboard() {
  const { currentRoute } = useRoutesContext();

  const defaultValues = {
    attributeTypes: ['dob'],
    dateRange: {
      end: dayjs(new Date()).format('MM/DD/YYYY'),
      start: dayjs(new Date()).subtract(1, 'year').format('MM/DD/YYYY'),
    },
    frequency: 'WEEKS',
    recordType: 'cip',
  };

  const [hasData, setHasData] = useState(false);

  const form = useForm({ defaultValues });

  const { isSubmitting, isValid } = form.formState;

  const [previouslySubmittedValues, setPreviouslySubmittedValues] = useState(defaultValues);

  const [isLoading, setIsLoading] = useState(false);
  const [theQueries, setTheQueries] = useState([
    {
      apiKey: 'RiskRecord',
      payload: {
        attributeType: defaultValues?.attributeTypes[0],
        beginDate: dayjs(defaultValues?.dateRange?.start).format('YYYY-MM-DD'),
        endDate: dayjs(defaultValues?.dateRange?.end).format('YYYY-MM-DD'),
        limitToCIPIngested: defaultValues?.recordType === 'cip' ? 'true' : 'false',
        timeUnit: defaultValues?.frequency,
      },
      queryKey: `GetRiskTrendReport-${defaultValues?.attributeTypes[0]}`,
    },
  ]);

  const { useApiQueries } = useApi();

  const { assets, isLoadingAssets } = useAssets([
    {
      apiKey: 'Default',
      queryKey: 'ListChronoUnits',
    },
    {
      apiKey: 'Event',
      queryKey: 'ListEventTypes',
    },
  ]);

  const queries = useApiQueries(theQueries);

  /**
   * Determines the overall loading state of all queries.
   */
  useEffect(() => {
    if (
      !isEmpty(queries) &&
      Object.keys(queries).length === theQueries.length &&
      isEmpty(
        Object.keys(queries).filter((queryKey) => {
          const query = queries[queryKey];
          return !(!query?.isLoading && !query?.isFetching);
        })
      )
    ) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [queries, theQueries]);

  useEffect(() => {
    if (!isEmpty(queries)) {
      const valuesFound = [];
      Object.keys(queries).forEach((queryKey) => {
        const query = queries[queryKey];

        if (isEmpty(query?.data?.series)) {
          valuesFound.push(false);
        } else {
          valuesFound.push(true);
        }
      });
      pull(valuesFound, false);
      if (!isEmpty(valuesFound)) {
        setHasData(true);
      } else {
        setHasData(false);
      }
    }
  }, [queries]);

  /**
   * handleSubmit
   *
   * Calls a remote API to save the form data into a database.
   *
   * @returns {Promise<void>}
   */
  const handleSubmit = useCallback(
    (submittedValues) => {
      if (!isSubmitting && isValid) {
        setPreviouslySubmittedValues(submittedValues);
        const queriesList = [];

        submittedValues?.attributeTypes.forEach((attributeType) => {
          const payload = {
            attributeType,
            beginDate: dayjs(submittedValues?.dateRange?.start).format('YYYY-MM-DD'),
            endDate: dayjs(submittedValues?.dateRange?.end).format('YYYY-MM-DD'),
            limitToCIPIngested: submittedValues?.recordType === 'cip' ? 'true' : 'false',
            timeUnit: submittedValues?.frequency,
          };

          queriesList.push({
            apiKey: 'RiskRecord',
            payload,
            queryKey: `GetRiskTrendReport-${attributeType}`,
          });
        });
        setTheQueries(queriesList);
      }
    },
    [isSubmitting, isValid, theQueries]
  );

  return (
    <ErrorHandler location="src/pages/private/Dashboards/OperationsDashboard/OperationsDashboard.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <FormProvider autoComplete="off" highlighted onSubmit={handleSubmit} state={form}>
          <Grid>
            <Grid.Col span={{ xs: '100%' }}>
              <Heading offset={0}>{currentRoute?.screenTitle}</Heading>
            </Grid.Col>
            {isLoadingAssets ? (
              <Grid.Col span={{ xs: '100%' }}>
                <Loader verticalAlignment="start" />
              </Grid.Col>
            ) : (
              <React.Fragment>
                <Grid.Col span={{ xs: '100%' }}>
                  <Filters assets={assets} form={form} />
                </Grid.Col>
                <Grid.Col span={{ xs: '100%' }}>
                  {isLoading || isSubmitting ? (
                    <Loader verticalAlignment="start" />
                  ) : (
                    <React.Fragment>
                      {hasData ? (
                        <Tabs
                          assets={assets}
                          form={form}
                          queries={queries}
                          submittedValues={previouslySubmittedValues}
                        />
                      ) : (
                        <Alert title="No data found with specified filters." variant="info" />
                      )}
                    </React.Fragment>
                  )}
                </Grid.Col>
              </React.Fragment>
            )}
          </Grid>
        </FormProvider>
      </motion.div>
    </ErrorHandler>
  );
}
